import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import type { FC } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { v4 as uuid } from 'uuid'

import { useStartRunControllerStartRun } from '@/api/repository/repositoryComponents'
import type { Flow } from '@/api/repository/repositorySchemas'
import { DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { useSelectedApp } from '@/contexts/selectedApp'

interface RunFlowActionProps {
	flow: Flow
}

const RunFlowAction: FC<RunFlowActionProps> = ({ flow }) => {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const startRun = useStartRunControllerStartRun()
	const { selectedApp: app } = useSelectedApp()

	const onFlowRun = useCallback(async () => {
		const loadingToastId = toast.loading('Starting run...')

		try {
			if (!app) {
				throw new Error('No application selected')
			}

			await startRun.mutateAsync({
				pathParams: {
					id: app._id,
				},
				body: {
					parameters: {
						name: 'Custom Flow Run',
						actionRetryAttempts: 1,
						pageLoadTimeout: 3000,
						_id: null,
						path: '/',
						depth: 1,
						duration: 15,
						stopAfterFlows: true,
						stopOnFlowError: true,
						enableModeling: false,
						useDatasetsForForms: false,
						assets: {
							datasets: {},
							rules: [],
							data: [],
							activities: [],
						},
						testableDomains: [],
						extensions: {
							accessibility: false,
							brokenLinks: false,
							resources: false,
							performance: false,
							functional: false,
							fileTesting: false,
						},
						workQueue: [
							{
								id: uuid(),
								type: 'Flow',
								state: 'Todo',
								activityId: flow._id,
								label: flow.name,
							},
						],
					},
				},
			})

			queryClient.invalidateQueries()
			toast.dismiss(loadingToastId)
			toast.success(`Run started`)
			navigate(`/app/${app._id}/status`)
		} catch (error) {
			toast.dismiss(loadingToastId)
			toast.error(
				`Failed to start run.\n ${
					error instanceof Error ? error.message : error
				}`
			)
		}
	}, [app, startRun, navigate, queryClient, flow])

	return <DropdownMenuItem onClick={onFlowRun}>Run</DropdownMenuItem>
}

export default RunFlowAction
